// Колонка списка, ширину которой человек тянет мышью за правый край.
//
// ## Откуда
//
// 11.10.2026 финансовый директор заказчика про «Ведомости»: «слева названия объектов не
// помещаются и обрезаются, хотя свободного места на экране много. Можно расширить этот
// столбец, желательно чтобы его ширину можно было менять мышкой». Монитор у него 2560 точек, а
// колонка стояла 368: «"Компания Шлюмберже" Радиус-Сервис фе…» резалось посередине, справа
// пустовала половина экрана.
//
// ## Почему ширина не из темы
//
// Ширины колонок объявлены токенами (`list`, `list-names`), свободное число в разметке
// запрещено: оно разъедется по разделам за неделю. Здесь число задаёт не разметка, а человек
// рукой, под свой монитор. Умолчание остаётся токеном из классов колонки; своё число ложится
// поверх, только когда край потянули, и только в этом файле (`оформление.test.ts`, «размер из
// данных»).
//
// ## Где помнится
//
// На устройстве, как тема (`shared/theme`): ширина про монитор, а не про человека. Тот же
// финдиректор на ноутбуке хочет уже, чем за большим экраном. Двойной щелчок по краю
// возвращает ширину по умолчанию и забывает своё.
//
// ## Где не тянется
//
// В узком окне (портрет планшета) поверхность одна за раз и колонка во всю ширину: тянуть
// нечего, края нет, своё число не применяется.

import type { KeyboardEvent, PointerEvent, ReactNode } from 'react';
import { useRef, useState } from 'react';

import { useСреда } from '@/shared/среда';
import { cn } from '@/utils/cn';

/** Уже нельзя: четыре отбора колонки стоят парами и уже этого не помещаются. */
export const САМАЯ_УЗКАЯ = 320;
/** Шире незачем: названия переносятся и в меньшей, а сетке справа нужно место. */
export const САМАЯ_ШИРОКАЯ = 720;
/** Сколько оставить содержимому справа: меньше — и ведомость уже не прочитать. */
const МЕСТО_СПРАВА = 480;
/** Шаг стрелками на клавиатуре. */
const ШАГ = 16;

function ключХранения(ключ: string): string {
  return `bark.column-width.${ключ}`;
}

/** Своя ширина колонки на этом устройстве; `null` — не тянули, стоит умолчание. */
export function прочитатьШирину(ключ: string): number | null {
  try {
    const сырое = localStorage.getItem(ключХранения(ключ));
    if (сырое === null) {
      return null;
    }
    const число = Number(сырое);
    return Number.isFinite(число) && число >= САМАЯ_УЗКАЯ ? Math.min(число, САМАЯ_ШИРОКАЯ) : null;
  } catch {
    // Хранилище недоступно или в нём мусор: стоит умолчание, это не поломка.
    return null;
  }
}

function запомнить(ключ: string, ширина: number | null): void {
  try {
    if (ширина === null) {
      localStorage.removeItem(ключХранения(ключ));
    } else {
      localStorage.setItem(ключХранения(ключ), String(ширина));
    }
  } catch {
    // Не запомнили: в этом окне ширина та, что потянули, после перезапуска — умолчание.
  }
}

/** Ширина в допустимых пределах. `потолок` — сколько позволяет окно прямо сейчас. */
export function вПределах(ширина: number, потолок: number = САМАЯ_ШИРОКАЯ): number {
  const верх = Math.max(САМАЯ_УЗКАЯ, Math.min(САМАЯ_ШИРОКАЯ, потолок));
  return Math.round(Math.min(верх, Math.max(САМАЯ_УЗКАЯ, ширина)));
}

export function ТянущаясяКолонка({
  ключ,
  подпись,
  className,
  children,
}: {
  /** Под каким именем помнить ширину: у каждой колонки своя. */
  ключ: string;
  /** Что тянут — для чтения с экрана и подсказки: «Ширина списка ведомостей». */
  подпись: string;
  /** Классы колонки: ширина по умолчанию токеном, раскладка, поведение в узком окне. */
  className: string;
  children: ReactNode;
}) {
  const среда = useСреда();
  const тянется = среда.ширина === 'широко' || среда.ширина === 'средне';
  const колонка = useRef<HTMLElement>(null);
  const [ширина, setШирина] = useState<number | null>(() => прочитатьШирину(ключ));
  // Сколько точек колонка занимает на самом деле: у умолчания числа в коде нет, оно в теме.
  const [замер, setЗамер] = useState<number | null>(null);
  const захват = useRef<{ x: number; ширина: number; потолок: number } | null>(null);
  const последняя = useRef<number | null>(ширина);
  // Черта видна, пока тянут, а не только под курсором: курсор во время хода уходит с края.
  const [тянут, setТянут] = useState(false);
  // Сколько позволяет окно сейчас — для чтения с экрана; меряется, когда край берут в руки.
  const [предел, setПредел] = useState(САМАЯ_ШИРОКАЯ);

  function сейчас(): number {
    return колонка.current?.getBoundingClientRect().width ?? ширина ?? САМАЯ_УЗКАЯ;
  }

  /** Сколько позволяет окно: колонка не имеет права съесть место ведомости справа. */
  function потолок(): number {
    const ряд = колонка.current?.parentElement?.getBoundingClientRect().width;
    return ряд === undefined || ряд === 0 ? САМАЯ_ШИРОКАЯ : ряд - МЕСТО_СПРАВА;
  }

  function поставить(новая: number, верх: number): number {
    const итог = вПределах(новая, верх);
    последняя.current = итог;
    setШирина(итог);
    setЗамер(итог);
    return итог;
  }

  const нажали = (событие: PointerEvent<HTMLDivElement>) => {
    if (событие.button !== 0) {
      return;
    }
    // Без этого браузер начинает выделять текст списка и ведомости под курсором.
    событие.preventDefault();
    событие.currentTarget.setPointerCapture(событие.pointerId);
    захват.current = { x: событие.clientX, ширина: сейчас(), потолок: потолок() };
    setПредел(вПределах(САМАЯ_ШИРОКАЯ, захват.current.потолок));
    setТянут(true);
  };

  const ведут = (событие: PointerEvent<HTMLDivElement>) => {
    const начало = захват.current;
    if (начало === null) {
      return;
    }
    // Кнопку отпустили там, где край этого не услышал (переключились на другое окно посреди
    // хода): без этой проверки колонка ездила бы за простым наведением и не запоминалась.
    if (событие.buttons === 0) {
      отпустили();
      return;
    }
    поставить(начало.ширина + событие.clientX - начало.x, начало.потолок);
  };

  const отпустили = () => {
    if (захват.current === null) {
      return;
    }
    захват.current = null;
    setТянут(false);
    // Последнее значение из хода, а не из события: у отменённого касания координата нулевая,
    // и колонка схлопнулась бы до самой узкой.
    запомнить(ключ, последняя.current);
  };

  const клавиша = (событие: KeyboardEvent<HTMLDivElement>) => {
    const было = сейчас();
    const куда =
      событие.key === 'ArrowLeft'
        ? было - ШАГ
        : событие.key === 'ArrowRight'
          ? было + ШАГ
          : событие.key === 'Home'
            ? САМАЯ_УЗКАЯ
            : событие.key === 'End'
              ? САМАЯ_ШИРОКАЯ
              : null;
    if (куда === null) {
      return;
    }
    событие.preventDefault();
    запомнить(ключ, поставить(куда, потолок()));
  };

  const какБыло = () => {
    захват.current = null;
    setТянут(false);
    последняя.current = null;
    setШирина(null);
    setЗамер(null);
    запомнить(ключ, null);
  };

  return (
    <section
      ref={колонка}
      className={cn('relative', className)}
      // Своё число не шире, чем позволяет окно СЕЙЧАС: ширину тянули на большом мониторе, а
      // открыли программу на половине экрана ноутбука — ведомости справа всё равно остаётся
      // место. Самая узкая побеждает: уже неё колонка не сжимается ни при каком окне.
      style={
        тянется && ширина !== null
          ? { width: ширина, maxWidth: `calc(100% - ${МЕСТО_СПРАВА}px)`, minWidth: САМАЯ_УЗКАЯ }
          : undefined
      }
    >
      {children}
      {тянется && (
        <div
          role='separator'
          aria-orientation='vertical'
          aria-label={подпись}
          aria-valuemin={САМАЯ_УЗКАЯ}
          aria-valuemax={предел}
          aria-valuenow={ширина ?? замер ?? undefined}
          tabIndex={0}
          title={`${подпись}: потяните край, чтобы сделать шире или уже. Двойной щелчок вернёт как было.`}
          data-край-колонки=''
          onFocus={() => {
            setЗамер(Math.round(сейчас()));
            setПредел(вПределах(САМАЯ_ШИРОКАЯ, потолок()));
          }}
          onPointerDown={нажали}
          onPointerMove={ведут}
          onPointerUp={отпустили}
          onPointerCancel={отпустили}
          onLostPointerCapture={отпустили}
          onDoubleClick={какБыло}
          onKeyDown={клавиша}
          className={cn(
            // Край шире видимой черты: в черту в одну точку мышью не попасть.
            'group/край absolute inset-y-0 -right-1 z-20 flex w-2 cursor-col-resize touch-none select-none justify-center',
            'focus-visible:outline-none',
            // Пальцем на планшете в восемь точек не попасть тем более.
            'тач:-right-3 тач:w-6',
          )}
        >
          <span
            aria-hidden='true'
            className={cn(
              'h-full w-0.5 transition duration-200 ease-out',
              'group-hover/край:bg-primary-base group-focus-visible/край:bg-primary-base',
              тянут ? 'bg-primary-base' : 'bg-transparent',
            )}
          />
        </div>
      )}
    </section>
  );
}
